import { useGSAP } from "@gsap/react";
import clsx from "clsx";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Image from "next/image";
import { memo, useCallback, useRef, useEffect } from "react"
import { useApp } from "@/context/AppContext";

function RecordImageGroup({ data, ref, isIn, isHoverIn, onLoad }) {
  const artifactsRefs = useRef([]);
  const artifactsWrapperRefs = useRef([]);
  const root = useRef(null);
  const {currentTransitionName} = useApp();

  const computeParallax = useCallback((progress) => {
    artifactsWrapperRefs.current.forEach((ref) => {
      if (ref) {
        gsap.killTweensOf(ref)
        gsap.to(ref, {
          yPercent: -(progress * 2 - 1) * 3
        })
      }
    })
  }, [artifactsRefs])

  useGSAP(() => {
    if (isIn) {
      const mm = gsap.matchMedia();

      mm.add("(prefers-reduced-motion: no-preference)", () => {
        ScrollTrigger.create({
          trigger: root.current,
          start: 'top bottom+=20%',
          end: 'bottom top-=20%',
          onUpdate: (self) => {
            computeParallax(self.progress)
          }
        })
      })
    }
  }, [isIn])

  useGSAP(() => {
    const mm = gsap.matchMedia();

    mm.add("(prefers-reduced-motion: no-preference)", () => {
      artifactsRefs.current.forEach((ref, index) => {
        if (ref) {
          gsap.killTweensOf(ref, "scale");
          gsap.to(ref, {
            scale: isHoverIn ? 1.1 : 1,
            delay: 0,
            duration: isHoverIn ? 1.2 : 0.9,
            ease: 'expo.out'
          })
        }
      })
    })

  }, [isHoverIn])

  const skipAnimationRef = useRef(currentTransitionName === "overlay-out");
  
  // Clear skip flag after scroll restoration has settled
  useEffect(() => {
    if (skipAnimationRef.current) {
      const timer = setTimeout(() => {
        skipAnimationRef.current = false;
      }, 300);
      return () => clearTimeout(timer);
    }
  }, []);

  useGSAP(() => {
    const mm = gsap.matchMedia();

    mm.add("(prefers-reduced-motion: no-preference)", () => {
      artifactsRefs.current.forEach((ref, index) => {
        if (ref && isIn) {
          gsap.fromTo(ref, {
            y: 100
          }, {
            y: 0,
            duration: skipAnimationRef.current ? 0 : 1.2,
            delay: index * 0.1,
            ease: 'expo.out'
          })
        }
      })
    })

  }, [artifactsRefs, isIn, skipAnimationRef])

  return (
    <div className="w-full h-auto" style={{ aspectRatio: `${data.image.width}/${data.image.height}` }} ref={root}>
      <Image unoptimized={true} onLoad={onLoad} className={clsx("w-full h-auto relative z-10 opacity-0")}
        width={data.image.width}
        height={data.image.height}
        src={data.image.src}
        alt={data.image.alt}
        ref={ref}
      />
      {[data.artifact_1, data.artifact_2].map((artifact, index) => (
        artifact && (
          <div
            key={data.slug + artifact.src}
            className={clsx("w-full h-auto absolute top-0 left-0 pointer-events-none", artifact.pos === 'front' ? 'z-20' : 'z-0')}
            ref={(el) => artifactsWrapperRefs.current[index] = el}
          >
            <Image unoptimized={true}
              className="w-full h-full"
              width={artifact.width}
              height={artifact.height}
              src={artifact.src}
              alt={artifact.alt}
              aria-hidden={true}
              ref={(el) => artifactsRefs.current[index] = el}
            />
          </div>
        )
      ))}
    </div>
  )
}

export default memo(RecordImageGroup)